import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Textarea } from '../../components/ui/Textarea';
import { completeStudentProfile } from '../../modules/platform/api/onboarding';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { ROUTES } from '../../utils/constants';

interface StudentProfileFormData {
  bio: string;
  interests?: string;
}

export const StudentProfilePage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<StudentProfileFormData>();

  const onSubmit = async (data: StudentProfileFormData) => {
    setIsLoading(true);
    try {
      await completeStudentProfile({
        bio: data.bio,
        interests: data.interests?.split(',').map((i) => i.trim()),
      });
      showSuccessToast('Profile completed successfully!');
      navigate(ROUTES.STUDENT_DASHBOARD);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout showSidebar={false}>
      <PageHeader
        title="Complete Your Profile"
        subtitle="Tell us about yourself"
      />

      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Student Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Textarea
              label="About Me"
              {...register('bio', {
                required: 'Bio is required',
                minLength: { value: 20, message: 'Bio must be at least 20 characters' },
              })}
              error={errors.bio?.message}
              placeholder="Tell us about yourself, your hobbies, and what you like to learn..."
              rows={4}
            />

            <Textarea
              label="Interests (comma-separated)"
              {...register('interests')}
              error={errors.interests?.message}
              placeholder="Tamil language, Math, Science, Art..."
              rows={2}
              helperText="Separate multiple interests with commas"
            />

            <div className="flex gap-4 pt-4">
              <Button type="submit" isLoading={isLoading} className="flex-1">
                Complete Profile
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </Layout>
  );
};

